import { useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { FileImport } from './FileImport';
import { parsePhases, type PhaseImport } from './phaseFiles';
import type { Plan } from './types';

export function PhaseExchange({ plan, pipeCount, canWrite, onUpdate }: {
  plan: Plan; pipeCount: number; canWrite: boolean; onUpdate: (changes: Partial<Plan>) => void;
}) {
  const [preview, setPreview] = useState<PhaseImport | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [base, setBase] = useState('');
  return <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <NJText as="h4" scale="sm">Échange Excel · CSV</NJText>
    <NJText scale="xs">Modifiez la colonne annee (2027–2045). Pour imposer une date de canalisation, indiquez mode = manuel. Les identifiants restent inchangés. Aucun fichier n’est envoyé sur un serveur.</NJText>
    {canWrite && <FileImport id="phase-import" label="Réimporter le phasage CSV" accept=".csv,text/csv"
      disabled={busy} onFile={async (file) => {
        setPreview(null); setMessage(''); setBusy(true);
        try {
          const parsed = parsePhases(await file.text(), plan, pipeCount);
          setPreview(parsed);
          setBase(JSON.stringify(plan));
        } catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
        finally { setBusy(false); }
      }} />}
    {busy && <NJText role="status" scale="xs">Lecture du fichier…</NJText>}
    {message && <NJText role="status" scale="xs">{message}</NJText>}
    {preview && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <NJText scale="sm">{preview.changes.length} lignes reconnues · {preview.errors.length} erreurs. Rien n’est appliqué avant confirmation.</NJText>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {preview.errors.map((error) => <NJText key={error} scale="xs" role="alert">{error}</NJText>)}
        {preview.changes.map((change) => <NJText key={change} scale="xs">{change}</NJText>)}
      </div>
      <NJButton label="Appliquer les dates importées" variant="primary" scale="sm"
        disabled={!canWrite || !!preview.errors.length || !preview.changes.length || busy} onClick={() => {
          if (base !== JSON.stringify(plan)) {
            setPreview(null); setMessage('Le plan a changé depuis la lecture. Réimportez le fichier pour vérifier les nouvelles valeurs.'); return;
          }
          const overrides = { ...plan.pipePhaseOverrides, ...preview.pipes };
          preview.automaticPipes.forEach((index) => { delete overrides[index]; });
          onUpdate({ buildingPhases: { ...plan.buildingPhases, ...preview.buildings }, pipePhaseOverrides: overrides });
          setMessage(`${preview.changes.length} lignes appliquées au brouillon. Confirmez les modifications pour les conserver.`);
          setPreview(null);
        }} />
      <NJButton label="Annuler l’import" variant="secondary" scale="sm" onClick={() => setPreview(null)} />
    </div>}
  </section>;
}
